import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, formatDistanceStrict, fromUnixTime, parseISO } from "date-fns";
import { Button, CircularProgress } from "@nextui-org/react";
import { MdOutlinePhoneCallback, MdOutlinePhoneForwarded } from "react-icons/md";
import getConfig from "../../../conf/config";
import myAxios from "../../../utils/axiosWrapper";
import {
  getDateFnsLocale,
  transformFromUTC,
  transformFromUTCFromISO,
} from "../../../utils/dateUtils";

// Calls before / after this one, as returned by the API on each side.
const RELATED_CALLS_LIMIT = 3;

const GRADE_DOT_CLASS = {
  green: "bg-grade-high",
  orange: "bg-grade-medium",
  red: "bg-grade-low",
};

const toDate = (call_timestamp) => {
  if (!call_timestamp) return null;
  return typeof call_timestamp === "string"
    ? parseISO(call_timestamp)
    : fromUnixTime(call_timestamp > 1e12 ? call_timestamp / 1000 : call_timestamp);
};

// The modal item comes from Algolia (epoch seconds) or from the call deep link
// (ISO string); related calls are always epoch seconds.
const formatInTimezone = (call_timestamp, display_tz) =>
  format(
    typeof call_timestamp === "string"
      ? transformFromUTCFromISO(call_timestamp, display_tz)
      : transformFromUTC(call_timestamp, display_tz),
    "dd/MM HH:mm"
  );

const hasUsableNumber = (client_number) =>
  !!client_number && client_number !== "None" && client_number.replace(/\D/g, "").length >= 8;

const RelatedCallRow = ({ call, current_date, display_tz }) => {
  const { t, i18n } = useTranslation();
  const call_date = toDate(call.call_timestamp);
  const is_before = current_date && call_date < current_date;
  const DirectionIcon = call.incoming ? MdOutlinePhoneCallback : MdOutlinePhoneForwarded;

  return (
    <button
      type="button"
      onClick={() => window.open(`${getConfig().BASE_URL}/calls/${call.id}`, "_blank")}
      className="w-full flex items-start gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-gray-100 transition-colors"
    >
      <DirectionIcon
        size={16}
        className="text-gray-400 mt-0.5 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-gray-900 whitespace-nowrap">
            {formatInTimezone(call.call_timestamp, display_tz)}
          </span>
          {current_date && (
            <span className="text-[11px] text-gray-400 whitespace-nowrap">
              {`${formatDistanceStrict(call_date, current_date, {
                locale: getDateFnsLocale(i18n.language),
              })} ${is_before ? t("before") : t("after")}`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${
              GRADE_DOT_CLASS[call.colored_grade] || "bg-gray-300"
            }`}
          />
          <span className="text-[11px] text-gray-500 truncate">
            {[call.call_purpose, call.operator_name].filter(Boolean).join(" · ")}
          </span>
        </div>
      </div>
    </button>
  );
};

const RelatedCallsBlock = ({ item, display_tz }) => {
  const { t } = useTranslation();
  const [related, setRelated] = useState(null);
  const [is_loading, set_is_loading] = useState(false);

  const client_number = item?.client_number;
  const can_match = hasUsableNumber(client_number);

  useEffect(() => {
    if (!item?.id || !can_match) {
      setRelated(null);
      return undefined;
    }

    let is_cancelled = false;
    set_is_loading(true);
    myAxios
      .get(`${getConfig().API_URL}/audio/api/v1/calls/${item.id}/related`, {
        params: { limit: RELATED_CALLS_LIMIT },
      })
      .then((response) => {
        if (!is_cancelled) setRelated(response?.data || null);
      })
      .catch((error) => {
        console.error("Error fetching related calls:", error);
        if (!is_cancelled) setRelated(null);
      })
      .finally(() => {
        if (!is_cancelled) set_is_loading(false);
      });

    return () => {
      is_cancelled = true;
    };
  }, [item?.id, can_match]);

  if (!can_match) {
    return null;
  }

  const current_date = toDate(item?.call_timestamp);
  const before = related?.before || [];
  const after = related?.after || [];
  const total = related?.total || 0;

  const open_all_client_calls = () => {
    const params = new URLSearchParams({ filter: "client_number", params: client_number });
    window.open(`${getConfig().BASE_URL}/calls?${params.toString()}`, "_blank");
  };

  return (
    <div className="pt-3 border-t border-gray-100">
      <p className="text-xs text-gray-400 mb-2 px-2">
        {t("Calls from the same client")}
        {total > 0 && <span className="ml-1">· {total}</span>}
      </p>

      {is_loading && (
        <div className="flex justify-center py-3">
          <CircularProgress
            size="sm"
            aria-label={t("Loading...")}
          />
        </div>
      )}

      {!is_loading && related && total === 0 && (
        <p className="text-xs text-gray-400 px-2 pb-2">{t("No other call from this number")}</p>
      )}

      {!is_loading && total > 0 && (
        <>
          {related?.is_shared_number && (
            <p className="text-[11px] text-warning-600 bg-warning-50 rounded-md px-2 py-1.5 mb-2">
              {t("Shared number (switchboard?): these calls may not all be from the same client")}
            </p>
          )}
          <div className="flex flex-col">
            {before.map((call) => (
              <RelatedCallRow
                key={call.id}
                call={call}
                current_date={current_date}
                display_tz={display_tz}
              />
            ))}
            <div className="flex items-center gap-2 px-2 py-1.5 my-0.5 rounded-lg bg-primary/10">
              <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
              <span className="text-xs font-semibold text-primary">
                {t("This call")}
                {current_date && ` · ${formatInTimezone(item.call_timestamp, display_tz)}`}
              </span>
            </div>
            {after.map((call) => (
              <RelatedCallRow
                key={call.id}
                call={call}
                current_date={current_date}
                display_tz={display_tz}
              />
            ))}
          </div>
          <Button
            size="sm"
            variant="bordered"
            className="w-full mt-3 border-gray-200 text-gray-700"
            onPress={open_all_client_calls}
          >
            {t("See all calls from this client")}
          </Button>
        </>
      )}
    </div>
  );
};

export default RelatedCallsBlock;
